import React, { createContext, useContext } from 'react';

import { indexOf, last } from 'ramda';
import { useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';

import { PAGE_PATH } from 'constants/routes';
import { CONTENT_LIBRARY_PATH } from 'constants/routes';
import { useAccountPath } from 'hooks/useAccountPath';
import { isAgentSelector } from 'store/user/selectors';
import { mapLibType2LibPath } from 'utils/map';

import {
  useDeletePageHandler,
  useSavePageHandler,
  usePublishPageHandler,
  useNextStepHandler,
  useNextStepFormHandler,
} from './handlers';
import { mapLibType2BackButtonText, mapPageType2Steps, STEPS } from './map';

const mapStepParamName2Step = (stepParamName) => {
  if (stepParamName === 'landing') return STEPS.PAGE;
  return stepParamName;
};

const getNextStep = (steps, currentStep) => {
  const currentStepIdx = indexOf(currentStep, steps);

  if (currentStepIdx === -1) {
    return null;
  }

  return steps[currentStepIdx + 1] || null;
};

const PAGE_WITH_STEP_PATH = PAGE_PATH + '/:step';

export function PageContextProvider({ children, page }) {
  const { libType, pageId } = useParams();
  const isCreating = pageId === '0';
  const libURL = mapLibType2LibPath(libType);
  const isAgent = useSelector(isAgentSelector);
  const getPath = useAccountPath();
  const currentStep = mapStepParamName2Step(useRouteMatch(PAGE_WITH_STEP_PATH)?.params?.step);
  const steps = mapPageType2Steps(page.type, { isAgent });

  /**
   * @type {PageContext}
   */
  const data = {
    backButton: {
      link: getPath(libURL),
      isSSR: getPath(libURL) === getPath(CONTENT_LIBRARY_PATH),
      text: isCreating ? null : mapLibType2BackButtonText(libType, page.name, page.type),
    },
    steps,
    currentStep,
    nextStep: getNextStep(steps, currentStep),
    lastStep: last(steps),
    isCreating,
    useNextStepHandler,
    useNextStepFormHandler,
    useSavePageHandler,
    usePublishPageHandler,
    useDeletePageHandler,
  };

  return <PageContext.Provider value={data}>{children}</PageContext.Provider>;
}

const PageContext = createContext(null);

/**
 * Page context data
 * @typedef {object} PageContext
 * @property {object} backButton data of the back button
 * @property {string} backButton.link link of back button
 * @property {string} backButton.text text of back button
 * @property {string[]} steps available steps for the current page
 * @property {string} currentStep current step
 * @property {string} nextStep next step
 * @property {string} lastStep last step
 * @property {boolean} isCreating is page in the create mode?
 * @property {function} useNextStepHandler next page handler hook
 * @property {function} useNextStepFormHandler next page handler hook for form submission
 * @property {function} useSavePageHandler save page handler hook
 * @property {function} usePublishPageHandler publish page handler hook
 * @property {function} useDeletePageHandler delete page handler hook
 */

/**
 * @returns {PageContext}
 */
export const usePageContext = () => useContext(PageContext);
